import React from 'react';
import { Users, Award, Calendar, Vote, TrendingUp, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';

const Dashboard: React.FC = () => {
  const { data: stats, loading, error } = useApi(() => apiService.getDashboardStats());

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-400">
        <AlertCircle className="mr-2 inline h-5 w-5" />
        {error.message}
      </div>
    );
  }

  // Tailwind CSS requires full class names at build time - dynamic construction doesn't work
  const colorMap: Record<string, { bg: string; text: string }> = {
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-400' },
    green: { bg: 'bg-green-500/10', text: 'text-green-400' },
    purple: { bg: 'bg-purple-500/10', text: 'text-purple-400' },
    yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
    orange: { bg: 'bg-orange-500/10', text: 'text-orange-400' },
  };

  const statCards = [
    {
      label: 'Total Crew',
      value: stats?.total_crew || 0,
      icon: Users,
      color: 'sky',
      trend: '+12%',
    },
    {
      label: 'Active Crew',
      value: stats?.active_crew || 0,
      icon: TrendingUp,
      color: 'green',
      trend: '+5%',
    },
    {
      label: 'Qualifications',
      value: stats?.total_qualifications || 0,
      icon: Award,
      color: 'purple',
      trend: '+8%',
    },
    {
      label: 'Expiring Soon',
      value: stats?.expiring_soon || 0,
      icon: AlertCircle,
      color: 'yellow',
      trend: '-3%',
    },
    {
      label: 'Roster Assignments',
      value: stats?.roster_assignments || 0,
      icon: Calendar,
      color: 'blue',
      trend: '+15%',
    },
    {
      label: 'Pending Bids',
      value: stats?.pending_bids || 0,
      icon: Vote,
      color: 'orange',
      trend: '+7%',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-400">
          Overview of CREW module operations and key metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card group relative overflow-hidden">
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-sky-500/10 to-sky-600/5 opacity-0 transition-opacity group-hover:opacity-100"></div>

            <div className="relative flex items-start justify-between">
              <div>
                <p className="stat-label mb-2">{stat.label}</p>
                <p className="stat-value">{stat.value.toLocaleString()}</p>
                <div className="mt-2 flex items-center gap-1 text-sm text-green-400">
                  <TrendingUp className="h-4 w-4" />
                  <span>{stat.trend}</span>
                </div>
              </div>

              <div className={`rounded-lg ${colorMap[stat.color]?.bg || 'bg-sky-500/10'} p-3`}>
                <stat.icon className={`h-6 w-6 ${colorMap[stat.color]?.text || 'text-sky-400'}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="glass-card p-6">
        <h2 className="mb-4 text-xl font-semibold text-white">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => window.location.href = '/personnel'}
            className="btn-primary flex items-center justify-center gap-2"
          >
            <Users className="h-5 w-5" />
            Add Personnel
          </button>
          <button
            onClick={() => window.location.href = '/roster'}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <Calendar className="h-5 w-5" />
            Create Roster
          </button>
          <button
            onClick={() => window.location.href = '/qualifications'}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <Award className="h-5 w-5" />
            Add Qualification
          </button>
          <button
            onClick={() => window.location.href = '/bidding'}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <Vote className="h-5 w-5" />
            Review Bids
          </button>
        </div>
      </div>

      {/* Recent Activity - TODO: Connect to real audit log data */}
      <div className="glass-card p-6">
        <h2 className="mb-4 text-xl font-semibold text-white">Recent Activity</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-center py-8 text-gray-400">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No recent activity</p>
              <p className="text-xs mt-1">Activity will appear here as changes are made</p>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-800">
          <button
            onClick={() => window.location.href = '/audit'}
            className="text-sm text-sky-400 hover:text-sky-300"
          >
            View full audit log
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
